import type { ProductsOnSaleQuery } from '#graphql-operations'
import type { CollectionCardItem } from '~/components/collection/ProductGrid.vue'
import { getSalePrice } from '~/utils/price'

export type SaleProduct = ProductsOnSaleQuery['productsOnSale']['items'][number]

/**
 * Maps a `productsOnSale` product to a collection card, with its pre-sale price
 * struck through and its sale badge. Shared by the Sale page and the Black
 * Friday landing page's rail, so both price a sale product the same way.
 *
 * `productsOnSale` already carries the variant pricing, so pass the result to
 * `useCardsWithSwatches(…, { salePricing: false })`.
 */
export function useSaleProductCard() {
  const priceFormatter = usePriceFormatter()
  const saleBadge = useSaleBadge()

  return function toCard(p: SaleProduct): CollectionCardItem {
    const variant = p.variants?.[0]
    const sale = variant ? getSalePrice(variant) : null
    const oldPrice = sale ? priceFormatter(sale.gross, variant!.currency) : undefined
    const badge = saleBadge(sale?.savedPercent)
    const rating = p.customFields?.reviewRating && p.customFields?.reviewCount
      ? { stars: p.customFields.reviewRating, count: p.customFields.reviewCount }
      : undefined
    return {
      id: p.id,
      // `productsOnSale` doesn't return a top-level productVariantId — fall
      // back to the first variant's id so WishlistToggle can render on the card.
      productVariantId: variant?.id,
      name: p.name,
      primaryImage: p.featuredAsset?.preview,
      altImage: p.assets?.find(a => a.preview && a.preview !== p.featuredAsset?.preview)?.preview,
      price: variant ? priceFormatter(variant.priceWithTax, variant.currency) : '',
      priceAmount: variant?.priceWithTax,
      currency: variant?.currency,
      oldPrice,
      // Raw, so an active campaign coupon can measure its total saving from the
      // pre-sale price rather than from the already-discounted one.
      oldPriceAmount: sale?.gross,
      saleBadge: badge,
      savedPercent: sale?.savedPercent,
      rating,
      href: p.slug ? `/products/${p.id}/${p.slug}` : `/products/${p.id}`,
      inStock: p.variants?.some(v => v.stockLevel !== 'OUT_OF_STOCK') ?? true,
    }
  }
}
